<template>
  <div data-test-id="CaseTransferRequestComponent">
    <b-row class="bottom-bordered" data-test-id="CaseTransferRow">
      <div class="col text-left">
        <span class="text-white">Case Transfer</span>
      </div>
      <div class="col text-right">
        <span
          class="text-red"
          data-test-id="caseTransferRequestButton"
          role="button"
          tabindex="0"
          @click="onCaseTransferRequestClick"
          >Tap to Receive Case</span
        >
      </div>
    </b-row>

    <b-modal
      ref="case-transfer-request-modal"
      hide-header-close
      :hide-footer="caseTransfer && caseTransfer.request !== null"
      data-test-id="case-transfer-request-modal"
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <template #modal-header>
        <h5 class="m-0">Case Transfer</h5>
        <b-button
          size="sm"
          variant="link"
          class="text-danger p-0"
          data-test-id="caseTransferRequestCancelButton"
          @click="onCaseTransferRequestCancel()"
        >
          Cancel
        </b-button>
      </template>

      <div v-if="!caseTransfer || !caseTransfer.request || caseTransfer.request.status === ''">
        <div class="text-center mb-2">Enter case number to request case.</div>
        <b-form id="receive_case__form" @submit.prevent="onCaseTransferRequest()">
          <b-form-group>
            <b-form-input
              id="case-transfer-request__number"
              v-model="form.caseTransferCode"
              type="text"
              class="text-center"
              data-test-id="caseTransferRequestNumber"
              placeholder="Case transfer code"
              autocomplete="off"
            />
          </b-form-group>
        </b-form>
      </div>
      <div v-if="caseTransfer && caseTransfer.request" class="text-center">
        <b-spinner small />
        <div v-if="caseTransfer && caseTransfer.messageForReceiver" class="text-center mt-2 mb-2">
          {{ caseTransfer.messageForReceiver }}
        </div>
      </div>

      <template v-if="!caseTransfer || !caseTransfer.request" #modal-footer>
        <b-button
          id="case-transfer-request-send__button"
          variant="danger"
          block
          class="mt-2"
          :disabled="notOkToSendRequest()"
          @click.prevent="onCaseTransferRequest()"
          >Send Request</b-button
        >
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { LoggerService as lgg } from "vue-components-library";
import PubNub from "pubnub";

const NO_INTERNET_MESSAGE =
  "You must have an active network connection to use Case Transfer. Please try again when online.";
const NO_INTERNET_WHILE_IN_TRANSFER = "Slow or no internet connection. Please check your internet settings.";
const TRANSFER_EXPIRED_MESSAGE = "Case Transfer has expired.";

export default {
  name: "CaseTransferRequest",
  data() {
    return {
      displayMessage: undefined,
      isListening: false,
      isOnline: true,
      pubNubInstance: undefined,
      timeout: undefined,
      form: {
        caseTransferCode: "",
      },
      sendingRequest: false,
    };
  },
  computed: {
    ...mapGetters("PubNubSubscription", {
      pubNubSub: "pubNubSub",
    }),
    ...mapGetters("Login", ["user", "userId"]),
    ...mapGetters("TeamSelection", ["selectedTeam"]),
    ...mapGetters("TruckSelection", ["selectedTruck"]),
    ...mapGetters("CaseTransfer", ["caseTransfer", "caseTransferType", "caseTransferCase"]),
  },
  watch: {
    caseTransfer: function (newValue) {
      this.manageStatus(newValue);
    },
  },
  mounted() {
    this.manageStatus(this.caseTransfer);
  },
  methods: {
    ...mapActions("CaseTransfer", [
      "request",
      "requestCancel",
      "state",
      "received",
      "receiveCase",
      "loadCase",
      "clear",
    ]),
    ...mapActions("Global", ["checkOnlineStatus"]),
    async onCaseTransferRequestClick() {
      await this.clear();
      const isOnline = await this.checkOnlineStatus();
      if (isOnline) {
        this.showModal();
      } else {
        this.showWarningAlert("Warning", NO_INTERNET_MESSAGE);
      }
    },
    notOkToSendRequest() {
      return this.form.caseTransferCode ? this.sendingRequest : true;
    },
    async onCaseTransferRequest() {
      this.sendingRequest = true;
      const isOnline = await this.checkOnlineStatus();
      if (isOnline) {
        if (this.form.caseTransferCode) {
          try {
            await this.request({
              caseTransferCode: this.form.caseTransferCode,
              payload: {
                emsAgencyId: this.selectedTeam.id,
                truckId: this.selectedTruck.id,
              },
            });
            this.sendingRequest = false;
          } catch (err) {
            this.showWarningAlert("Warning", "Case not available for transfer.");
            lgg.info("Error", err);
            this.sendingRequest = false;
          }
        }
      } else {
        this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
        this.sendingRequest = false;
      }
    },
    async onCaseTransferRequestCancel() {
      const isOnline = await this.checkOnlineStatus();
      if (isOnline) {
        if (this.caseTransfer && this.caseTransfer.status && this.caseTransfer.status === "open") {
          try {
            await this.requestCancel(this.caseTransfer._id);
          } catch (err) {
            this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
            lgg.info("Error", err);
          }
        } else {
          this.hideModal();
        }
      } else {
        this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
      }
    },
    async manageStatus(ct) {
      if (this.caseTransfer && this.caseTransfer.status && this.caseTransferType === "request") {
        if (ct.request && ct.request.status !== "") {
          if (ct.status === "expired") {
            this.hideModal();
            this.showWarningAlert("Case Transfer", TRANSFER_EXPIRED_MESSAGE);
            this.cleanUp();
            return;
          }
          if (ct.request.status === "open") {
            this.subscribeChannel();
            this.startInternetStatusListener();
            this.showModal();
          }
          if (ct.request.status === "accepted") {
            try {
              await this.receiveCase(this.caseTransfer.caseId);
              await this.received(this.caseTransfer._id);
            } catch (error) {
              this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
            }
          }
          if (ct.request.status === "denied") {
            this.cleanUp();
            this.showWarningAlert("Case Transfer", "The request was declined.");
          }
          if (ct.request.status === "cancelled") {
            this.cleanUp();
            this.showWarningAlert("Case Transfer", "The case transfer was cancelled.");
          }
          if (ct.status === "done" && ct.request.status === "done") {
            this.loadCase(this.caseTransferCase);
          }
        }

        if (ct.status === "cancelled") {
          this.cleanup();
          this.showWarningAlert("Case Transfer", "The case transfer was cancelled.");
        }
        if (ct.status === "expired") {
          this.cleanup();
          this.showWarningAlert("Case Transfer", "The case transfer expired.");
        }
      }
    },
    subscribeChannel() {
      if (
        this.caseTransfer &&
        this.caseTransfer.pubnubChannel &&
        this.pubNubSub &&
        this.pubNubSub.api &&
        this.pubNubSub.api.subscribe
      ) {
        const pubNubInstance = new PubNub({
          subscribeKey: this.pubNubSub.api.subscribe,
          userId: `case-transfer-request-${this.userId}`,
        });

        pubNubInstance.subscribe({
          channels: [this.caseTransfer.pubnubChannel],
        });

        pubNubInstance.addListener({
          message: (m) => {
            if (m && m.message) {
              if (m.message.type === "CASE_TRANSFER_UPDATED") {
                if (this.caseTransfer && this.caseTransfer._id) {
                  this.state(this.caseTransfer._id);
                }
              }
            }
          },
        });

        this.pubNubInstance = pubNubInstance;
      }
    },
    unSubscribeChannel() {
      if (this.pubNubInstance && this.caseTransfer && this.caseTransfer.pubnubChannel) {
        setTimeout(
          () =>
            this.pubNubInstance.unsubscribe({
              channels: [this.caseTransfer.pubnubChannel],
            }),
          2000,
        );
      }
    },
    startInternetStatusListener() {
      this.isListening = true;
      this.runInternetStatusListener();
    },
    runInternetStatusListener() {
      if (this.isListening) {
        setTimeout(async () => {
          const isOnline = await this.checkOnlineStatus();
          if (isOnline) {
            if (!this.isOnline) {
              if (this.caseTransfer && this.caseTransfer._id) {
                this.subscribeChannel();
                this.state(this.caseTransfer._id);
              }
              this.stopCaseTransferTimeout();
            }
          } else {
            this.startCaseTransferTimeout();
          }

          this.isOnline = isOnline;
          this.runInternetStatusListener();
        }, 5000);
      }
    },
    stopInternetStatusListener() {
      this.isListening = false;
    },
    startCaseTransferTimeout() {
      if (!this.timeout) {
        this.timeout = setTimeout(() => {
          this.cleanUp();
          this.showWarningAlert("Case Transfer", "The case transfer timed out and it was cancelled.");
        }, 60000);
      }
    },
    stopCaseTransferTimeout() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = undefined;
      }
    },
    cleanUp() {
      this.hideModal();
      this.unSubscribeChannel();
      this.stopInternetStatusListener();
      this.clear();
      this.form.caseTransferCode = "";
    },
    showModal() {
      this.$refs["case-transfer-request-modal"].show();
    },
    hideModal() {
      this.$refs["case-transfer-request-modal"].hide();
    },
    showWarningAlert(title, message, callback) {
      this.$bvModal
        .msgBoxOk(message, {
          title: title,
          size: "sm",
          buttonSize: "sm",
          okVariant: "primary",
          okTitle: "Ok",
          footerClass: "p-2",
          hideHeaderClose: true,
          centered: true,
          id: "noInternetAlert",
        })
        .then(() => {
          if (callback) {
            callback();
          }
        })
        .catch(() => {
          // console.log("logout error", error);
        });
    },
  },
};
</script>

<style scoped></style>
